import { useAuth } from "@clerk/clerk-react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { QuickSendContactsContext } from "contexts/QuickSendContacts";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  BentoBrand,
  BentoContact,
  EmailSequence,
  NewBentoContact,
} from "schemas/dashboard";

import AddNewContactDialog from "features/Influencer/QuickSendIndividualDrawer/AddNewContact";
import { fetcherAuth } from "utils/api";

import { SelectedContactMap } from "../schema";
import BrandContactRow from "./BrandContactRow";
import styles from "./styles";

const PAGE_SIZE = 10;

interface Props {
  emailSequences: EmailSequence[];
  selectedContacts: SelectedContactMap;
  onContactSelected: (
    bentoBrandId: number,
    contact: BentoContact | NewBentoContact | null,
  ) => void;
  onRemoveBrand: (bentoBrand: BentoBrand) => void;
  onNoContactBrandIdsChange: (brandIds: number[]) => void;
}

type BrandContactsState = {
  bestContact: BentoContact | null;
  selectableContacts: BentoContact[];
  loading: boolean;
};

type ContactsResponse = {
  bestContact: BentoContact | null;
  selectableContacts: BentoContact[];
};

const ReviewBrands = ({
  emailSequences,
  selectedContacts,
  onContactSelected,
  onRemoveBrand,
  onNoContactBrandIdsChange,
}: Props) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { getLastContacted, updateLastContacted } = useContext(
    QuickSendContactsContext,
  );

  const isSubscribed = Boolean(
    currentOrg?.isProUser ||
      currentOrg?.isAutopilotUser ||
      currentOrg?.isVaUser,
  );

  const brands = useMemo(
    () =>
      emailSequences
        .map((es) => es.bentoBrand)
        .filter((b): b is BentoBrand => !!b),
    [emailSequences],
  );

  const [page, setPage] = useState(1);
  const [brandContacts, setBrandContacts] = useState<
    Record<number, BrandContactsState>
  >({});
  const [expandedBrandId, setExpandedBrandId] = useState<number | null>(null);

  const [addContactBrand, setAddContactBrand] = useState<BentoBrand | null>(
    null,
  );
  const [newContact, setNewContact] = useState<NewBentoContact>({
    name: "",
    email: "",
    title: "",
  });
  const [userContactsByBrand, setUserContactsByBrand] = useState<
    Record<number, NewBentoContact[]>
  >({});

  const total = brands.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const visibleBrands = useMemo(
    () => brands.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [brands, page],
  );

  const selectedContactsRef = useRef(selectedContacts);
  selectedContactsRef.current = selectedContacts;

  useEffect(() => {
    const toFetch = visibleBrands.filter(
      (b) => brandContacts[b.id] === undefined,
    );
    if (toFetch.length === 0) return;

    setBrandContacts((prev) => {
      const next = { ...prev };
      for (const b of toFetch) {
        next[b.id] = {
          bestContact: null,
          selectableContacts: [],
          loading: true,
        };
      }
      return next;
    });

    for (const brand of toFetch) {
      fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-brands/${brand.id}/contacts`,
        "GET",
      )
        .catch((error) => {
          setErrorAlert(error);
          return { bestContact: null, selectableContacts: [] };
        })
        .then((res: ContactsResponse) => {
          const data = {
            bestContact: res?.bestContact ?? null,
            selectableContacts: res?.selectableContacts ?? [],
          };
          setBrandContacts((prev) => ({
            ...prev,
            [brand.id]: { ...data, loading: false },
          }));
          if (!(brand.id in selectedContactsRef.current) && data.bestContact) {
            onContactSelected(brand.id, data.bestContact);
          }
          const contactIds = data.selectableContacts
            .map((c) => c.id)
            .filter((id): id is number => typeof id === "number");
          if (contactIds.length > 0) updateLastContacted(contactIds);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleBrands, currentOrg?.id]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  useEffect(() => {
    const ids: number[] = [];
    for (const brand of brands) {
      const data = brandContacts[brand.id];
      if (!data || data.loading) continue;
      const userAdded = userContactsByBrand[brand.id] ?? [];
      if (data.selectableContacts.length === 0 && userAdded.length === 0) {
        ids.push(brand.id);
      }
    }
    onNoContactBrandIdsChange(ids);
  }, [brands, brandContacts, userContactsByBrand, onNoContactBrandIdsChange]);

  const handleRemoveBrand = (brand: BentoBrand) => {
    setBrandContacts((prev) => {
      const next = { ...prev };
      delete next[brand.id];
      return next;
    });
    setUserContactsByBrand((prev) => {
      const next = { ...prev };
      delete next[brand.id];
      return next;
    });
    if (expandedBrandId === brand.id) setExpandedBrandId(null);
    onRemoveBrand(brand);
  };

  const handleOpenAddContact = (brand: BentoBrand) => {
    setAddContactBrand(brand);
    setNewContact({ name: "", email: "", title: "" });
  };

  const handleCloseAddContact = () => setAddContactBrand(null);

  const handleConfirmNewContact = (contact: NewBentoContact) => {
    if (!addContactBrand) return;
    const brandId = addContactBrand.id;
    setUserContactsByBrand((prev) => {
      const existing = prev[brandId] ?? [];
      if (existing.some((c) => c.email === contact.email)) return prev;
      return { ...prev, [brandId]: [...existing, contact] };
    });
    onContactSelected(brandId, contact);
    handleCloseAddContact();
  };

  if (brands.length === 0) {
    return (
      <Typography
        sx={{ py: 4, textAlign: "center", color: "rgba(1,67,57,0.6)" }}
      >
        No brands selected.
      </Typography>
    );
  }

  const anyPageLoading = visibleBrands.some(
    (b) => brandContacts[b.id]?.loading !== false,
  );

  return (
    <>
      <Typography variant="body2" sx={{ mb: 1.5, color: "rgba(1,67,57,0.7)" }}>
        Review which contact will receive each email. Click a brand to change
        the contact or add your own.
      </Typography>

      <Box sx={styles.list}>
        {anyPageLoading && visibleBrands.every((b) => !brandContacts[b.id]) && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {visibleBrands.map((brand) => {
          const data = brandContacts[brand.id];
          const userAdded = userContactsByBrand[brand.id] ?? [];
          const loading = !data || data.loading;
          return (
            <BrandContactRow
              key={brand.id}
              brand={brand}
              selectedContact={selectedContacts[brand.id]}
              expanded={expandedBrandId === brand.id}
              loadingContacts={loading}
              selectableContacts={data?.selectableContacts ?? []}
              userAddedContacts={userAdded}
              isSubscribed={isSubscribed}
              getLastContacted={getLastContacted}
              onToggleExpand={() =>
                setExpandedBrandId((curr) =>
                  curr === brand.id ? null : brand.id,
                )
              }
              onSelectContact={(c) => onContactSelected(brand.id, c)}
              onAddOwnContact={() => handleOpenAddContact(brand)}
              onRemoveBrand={() => handleRemoveBrand(brand)}
            />
          );
        })}
      </Box>

      {pageCount > 1 && (
        <Box sx={styles.paginationBar}>
          <Typography sx={{ fontSize: 12, color: "rgba(1,67,57,0.6)" }}>
            Page {page} of {pageCount} · {total} brands
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <Box
                component="i"
                className="fa-regular fa-chevron-left"
                sx={{ mr: 0.5 }}
              />
              Prev
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={page >= pageCount}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            >
              Next
              <Box
                component="i"
                className="fa-regular fa-chevron-right"
                sx={{ ml: 0.5 }}
              />
            </Button>
          </Box>
        </Box>
      )}

      {addContactBrand && (
        <AddNewContactDialog
          open={!!addContactBrand}
          handleClose={handleCloseAddContact}
          handleConfirm={handleConfirmNewContact}
          contact={newContact}
          setContact={setNewContact}
        />
      )}
    </>
  );
};

export default ReviewBrands;
